import React from 'react';
import cn from 'classnames';

interface SelectableCardProps {
  active: boolean;
  onToggle: () => void;
  icon?: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  variant?: 'checkbox' | 'dot';
}

export const SelectableCard: React.FC<SelectableCardProps> = ({
  active,
  onToggle,
  icon,
  title,
  subtitle,
  children,
  variant = 'checkbox',
}) => (
  <div
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }}
    className={cn(
      'group relative flex cursor-pointer flex-col rounded-xl border p-4 transition-all duration-150',
      active
        ? 'border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/20 dark:bg-blue-900/20'
        : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800/40 dark:hover:border-blue-500/60'
    )}
  >
    <div className="flex items-start gap-3"     onClick={onToggle}
>
      {icon && (
        <span
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base transition-colors',
            active
              ? 'bg-blue-500/15 dark:bg-blue-500/25'
              : 'bg-slate-100 dark:bg-gray-700/60'
          )}
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-xs leading-snug text-title-page-color-300 dark:text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
      <div
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center transition-all',
          variant === 'checkbox' ? 'rounded-md' : 'rounded-full',
          active
            ? 'border-transparent bg-blue-500 text-white shadow-sm'
            : 'border border-slate-300 bg-white dark:border-gray-600 dark:bg-gray-800'
        )}
      >
        {active && (
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
    </div>

    {active && children && (
      <div
        className="mt-3 space-y-2 border-t border-slate-200 pt-3 dark:border-gray-700/60"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="group"
      >
        {children}
      </div>
    )}
  </div>
);
